import React from 'react';
import type { PersonInformationModifier } from '../../../components/molecules/PersonInformation';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { ISonURL } from '../../../types/IUrl';
import { Modifier } from '../../../lib/bemUtils/Modifier';
import { DeletedUser } from './partials/DeletedUser';
import { ProfileName } from './partials/ProfileName';
import { OrgUnit } from './partials/OrgUnit';
import { ProfileLink } from './partials/ProfileLink';
import { RoleNames } from './partials/RoleNames';
import { PhoneLink } from './partials/PhoneLink';
import { EmailLink } from './partials/EmailLink';
import { useHtmlValidId } from '../../../hooks/useHtmlValidId';
import { PhoneNumber } from './partials/PhoneNumber';

export type PersonalData = {
  profileName?: string;
  profileLink?: ISonURL;
  phoneLink?: ISonURL;
  phoneNumber?: string;
  emailLink?: ISonURL;
  roleNames?: string[];
  deletedUser?: boolean;
  orgUnit?: string;
  officeDenotation?: string;
  modifier?: Modifier<PersonInformationModifier>;
};

export function PersonalData({
  deletedUser,
  emailLink,
  modifier,
  orgUnit,
  phoneLink,
  phoneNumber,
  profileLink,
  profileName,
  roleNames,
  officeDenotation,
}: PersonalData) {
  // It's important to use glossary here, because the descriptions of the personal data is also used in profile page
  const { t } = useSonTranslation('glossary');
  const id = useHtmlValidId();

  return (
    <div className="personal-data font-regular-15-19">
      {deletedUser && !profileName ? (
        <DeletedUser key={`deletedUser-${id}`} dfn={t('name')} deletedUser={t('deletedUser')} />
      ) : null}
      {profileLink ? (
        <ProfileLink
          key={profileLink.url + profileLink.name}
          profileLink={profileLink}
          dfn={t('name')}
          modifier={modifier}
          officeDenotation={officeDenotation}
        />
      ) : null}
      {profileName && !profileLink ? (
        <ProfileName
          key={profileName}
          dfn={t('name')}
          profileName={profileName}
          deleted={deletedUser ? t('deleted') : null}
          officeDenotation={officeDenotation}
        />
      ) : null}
      {roleNames && roleNames.length ? (
        <RoleNames
          key={roleNames + (profileName || 'unknown')}
          roleNames={roleNames.join(', ')}
          dfn={roleNames.length > 1 ? t('role_plural') : t('role')}
        />
      ) : null}
      {phoneLink && phoneLink.url ? (
        <PhoneLink
          key={phoneLink.url + profileName}
          phoneLink={phoneLink}
          dfn={t('phoneNumber')}
          title={t('phoneNumberTitle')}
        />
      ) : null}
      {phoneNumber && !profileLink ? (
        <PhoneNumber key={phoneNumber} phoneNumber={phoneNumber} dfn={t('phoneNumber')} />
      ) : null}
      {emailLink ? <EmailLink key={emailLink.url} emailLink={emailLink} dfn={t('eMailAddress')} /> : null}
      {orgUnit ? <OrgUnit key={`${orgUnit}-${profileName}`} orgUnit={orgUnit} dfn={t('orgunit')} /> : null}
    </div>
  );
}
